


import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import ReactPaginate from 'react-paginate';
import { AiOutlineFullscreen, AiOutlineFullscreenExit } from 'react-icons/ai';
import scrollTop from '../components/scrollTop';
import Loading from '../components/Loading';
import axios from 'axios';
import APIEndPoints from '../middleware/APIEndPoints';

const Blogs = () => {
    const [loading, setLoading] = useState(true); // Loading state
    const [blogs, setBlogs] = useState([]);
    const [categories, setCategories] = useState([]);
    const [filteredBlogs, setFilteredBlogs] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [modal, setModal] = useState({ isOpen: false, src: '', type: '', title: '', isFullscreen: false });
    const itemsPerPage = 9;


    const getCatrgoriesURl = APIEndPoints.get_categories.url;
    const getBlogsURl = APIEndPoints.get_blogs.url;
    

    // Fetch categories and blogs when the component mounts
    useEffect(() => {
        scrollTop();
        fetchCategories();
        fetchBlogs();
    }, []);

    // Fetch Categories
    const fetchCategories = async () => {
        try {
            const res = await axios.get(getCatrgoriesURl); 
            setCategories(res.data);
        } catch (err) {
            console.error('Error fetching categories:', err);
        }
    };

    // Fetch Blogs
    const fetchBlogs = async () => {
        try {
            const res = await axios.get(getBlogsURl); 
            setBlogs(res.data);
            setFilteredBlogs(res.data); 
        } catch (err) {
            console.error('Error fetching blogs:', err);
        } finally {
            setLoading(false); 
        }
    };

    // Pagination
    const offset = currentPage * itemsPerPage;
    const currentItems = filteredBlogs.slice(offset, offset + itemsPerPage);
    const pageCount = Math.ceil(filteredBlogs.length / itemsPerPage);

    const fadeIn = (direction = 'up', delay = 0) => ({
        hidden: {
            y: direction === 'up' ? 20 : -20,
            opacity: 0,
        },
        show: {
            y: 0,
            opacity: 1,
            transition: {
                duration: 0.6,
                delay: delay,
            },
        },
    });

    const handleItemClick = (item) => {
        if (!item.media) return; 
        setModal({
            isOpen: true,
            src: item.media,
            type: item.media?.includes('mp4') ? 'video' : 'photo',
            title: item.title,
            isFullscreen: false
        });
    };

    const closeModal = () => {
        setModal({ isOpen: false, src: '', type: '', title: '', isFullscreen: false });
    };

    const toggleFullscreen = () => {
        setModal((prev) => ({ ...prev, isFullscreen: !prev.isFullscreen }));
    };

    const handleVideoEnd = () => {
        closeModal();
    };

    const handlePageClick = ({ selected }) => {
        setCurrentPage(selected);
    };

    return (
        <div className="container mx-auto px-4 py-16 ">
            <motion.div
                initial={{ opacity: 0, y: -80 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1.2, ease: [0.25, 0.25, 0.25, 0.75] }}
                className="text-center mb-20 space-y-12"
            >
                <h4 className="text-3xl lg:text-[3rem] uppercase text-accent font-medium mb-2 tracking-wide">My Blogs</h4>
                <h2 className="text-4xl lg:text-[4rem] font-bold mb-6">Explore My Thoughts and Experiences</h2>
                <p className="text-lg text-gray-400">
                    Dive into my collection of writings, stories, and insights. I cover a range of topics, from personal experiences to professional advice. Stay tuned to learn more and get inspired by my journey.
                </p>
            </motion.div>

            {/* Display the blogs */}
            <motion.div
                variants={fadeIn('up', 0.1)}
                initial="hidden"
                animate="show"
                className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 mb-12 rounded"
            >
                {currentItems.map((item, index) => (
                    <motion.div
                        key={item._id}
                        className="group relative overflow-hidden rounded-lg shadow-lg cursor-pointer"
                        whileHover={{ scale: 1.05 }}
                        variants={fadeIn('up', index * 0.1)}
                        onClick={() => handleItemClick(item)}
                    >
                        {item.media?.includes('mp4') ? (
                            <video
                                src={item.media}
                                className="w-full h-56 lg:h-72 object-cover transition-transform duration-300 group-hover:scale-110"
                                autoPlay
                                loop
                                muted
                            />
                        ) : item.media ? (
                            <img
                                src={item.media}
                                alt={item.title}
                                className="w-full h-56 lg:h-72 object-cover transition-transform duration-300 group-hover:scale-110"
                            />
                        ) : (
                            <div className="w-full h-56 lg:h-72 bg-gray-300 flex items-center justify-center">
                                <p>No media available</p>
                            </div>
                        )}
                        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <h3 className="text-white text-2xl font-semibold">{item.title}</h3>
                        </div>
                    </motion.div>
                ))}
            </motion.div>

            <ReactPaginate
                previousLabel={"Prev"}
                nextLabel={"Next"}
                breakLabel={"..."}
                breakClassName={"break-me"}
                pageCount={pageCount}
                marginPagesDisplayed={1}
                pageRangeDisplayed={3}
                onPageChange={handlePageClick}
                containerClassName={"pagination flex justify-center mt-8 space-x-1 sm:space-x-2"}
                pageClassName={"page-item"}
                pageLinkClassName={"text-sm sm:text-xl px-3 text-blue-300 rounded"}
                previousLinkClassName={"btn px-3 py-1 text-white text-xl rounded"}
                nextLinkClassName={"btn text-white text-xl px-3 py-1 rounded"}
                activeClassName={"btn bg-blue-500 text-white rounded"}
                previousClassName={currentPage === 0 ? "cursor-not-allowed text-gray-400" : ""}
                nextClassName={currentPage === pageCount - 1 ? "cursor-not-allowed text-gray-400" : ""}
            />

            {/* Modal */}
            {modal.isOpen && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-75 z-50">
                    <div className={`relative bg-black/80 border-white border-2 rounded-lg shadow-lg ${modal.isFullscreen ? 'w-full h-full' : 'p-4 w-full max-w-2xl'}`}>
                        <button
                            onClick={closeModal}
                            className="absolute top-2 right-2 text-white bg-red-600 rounded-full w-8 h-8 flex items-center justify-center z-50"
                        >
                            &times;
                        </button>
                        {modal.type === 'photo' ? (
                            <div className={`relative  ${modal.isFullscreen ? 'h-full' : 'h-[30rem]'}`}>
                                <img
                                    src={modal.src}
                                    alt={modal.title}
                                    className={`w-full h-full object-cover ${modal.isFullscreen ? '' : ''}`}
                                />
                                <div className="absolute top-4 left-4 text-cyan-400 bg-black/40 border-white/20 border-2 px-4 py-2 rounded text-2xl font-semibold">
                                    {modal.title}
                                </div>
                            </div>
                        ) : (
                            <div className="relative w-full h-[30rem] flex items-center justify-center">
                                <video
                                    src={modal.src}
                                    className="max-w-full h-full object-cover"
                                    controls
                                    autoPlay
                                    onEnded={handleVideoEnd}
                                />
                                <div className="absolute top-4 left-4 text-cyan-400 bg-black/40 border-white/20 border-2  px-4 py-2 rounded text-2xl font-semibold">
                                    {modal.title}
                                </div>
                            </div>
                        )}
                        {modal.type === 'photo' && (
                            <button
                                onClick={toggleFullscreen}
                                className="absolute bottom-4 right-4 text-white bg-gray-700 rounded-full w-10 h-10 flex items-center justify-center z-50"
                            >
                                {modal.isFullscreen ? <AiOutlineFullscreenExit size={20} /> : <AiOutlineFullscreen size={20} />}
                            </button>
                        )}
                    </div>
                </div>
            )}

            {loading && <Loading />}
        </div>
    );
};

export default Blogs;
